import React, { useEffect, useRef, useState } from 'react';
import { axios } from '../../lib/helpers/request';
import { t } from '@konrad/reweb-aem';
import PropTypes from 'prop-types';
import { Button, Icon, Text } from '../index';
import { SelectInput, TextInput, MultiSelectionCheckbox } from '../_shared';
import ContactUsFormSubmissionResult from '../_shared/ContactUsFormSubmissionResult';
import styles from './PDFDownloadHero.module.scss';
import cx from 'classnames';
import {
    DROPDOWN_PLACEHOLDER_TEXT,
    ERROR_CODES,
    FORM_ERROR_CODES,
    PHONE_MASK,
    RECAPTCHA_BRANDING_TEXT,
    MAILCHIMP_SUBSCRIPTION_STATUS,
    LEAD_ID_KEY,
} from '../../lib/constants';
import {
    dropdownRequired,
    getInitialInputState,
    getPostData,
    getMergeFieldsData,
    handleErrors,
    validateForm,
    validateInput,
    setOtherDetails,
} from '../../lib/helpers/form';
import { getRecaptchaToken, loadRecaptcha } from '../../lib/helpers/recatpcha';
import dataLayerService from '../../lib/services/dataLayerService';
import { v4 as uuidv4 } from 'uuid';

const PDFDownloadHero = (props) => {
    const {
        children,
        ctaAltText,
        ctaText,
        dropdown1Label,
        dropdown1Name,
        dropdown1Options,
        dropdown1Optional,
        dropdown1RequiredErrorMessage,
        dropdown2Label,
        dropdown2Name,
        dropdown2Options,
        dropdown2Optional,
        dropdown2RequiredErrorMessage,
        dropdown3Label,
        dropdown3Name,
        dropdown3Options,
        dropdown3Optional,
        dropdown3OtherOption,
        dropdown3RequiredErrorMessage,
        dropdown3OtherDetailsFieldLabel,
        dropdown3OtherDetailsFieldName,
        dropdown3OtherDetailsFieldPlaceholder,
        dropdown3OtherDetailsFieldRequiredErrorMessage,
        emailLabel,
        emailInvalidErrorMessage,
        emailRequiredErrorMessage,
        field1Label,
        field1Name,
        field1Optional,
        field1RequiredErrorMessage,
        field2Label,
        field2Name,
        field2Optional,
        field2RequiredErrorMessage,
        field3Label,
        field3Name,
        field3Optional,
        field3InvalidErrorMessage,
        field3RequiredErrorMessage,
        field4Label,
        field4Name,
        field4Optional,
        field4RequiredErrorMessage,
        formHeading,
        formName,
        formSubheading,
        newsletterPromptLabel,
        path,
        pdfPath,
        phoneLabel,
        phoneName,
        phoneOptional,
        phonePlaceholder,
        phoneInvalidErrorMessage,
        phoneRequiredErrorMessage,
        submitErrorTitle,
        submitErrorCtaText,
        submitErrorCtaAltText,
        submitSuccessTitle,
        submitSuccessText,
        submitSuccessCtaText,
        submitSuccessCtaAltText,
        subtitle,
        title,
        multiSelectionCheckboxLabel,
        multiSelectionCheckboxName,
        multiSelectionCheckboxOptions,
        multiSelectionCheckboxOptional,
        multiSelectionCheckboxRequiredErrorMessage,
    } = props;

    const fields = {
        field1: {
            id: field1Name,
            label: field1Label,
            required: !field1Optional,
            requiredErrorMessage: field1RequiredErrorMessage,
        },
        field2: {
            id: field2Name,
            label: field2Label,
            required: !field2Optional,
            requiredErrorMessage: field2RequiredErrorMessage,
        },
        phone: {
            id: phoneName,
            label: phoneLabel,
            placeholder: phonePlaceholder,
            required: !phoneOptional,
            invalidErrorMessage: phoneInvalidErrorMessage,
            requiredErrorMessage: phoneRequiredErrorMessage,
        },
        email: {
            id: 'email',
            label: emailLabel,
            required: true,
            invalidErrorMessage: emailInvalidErrorMessage,
            requiredErrorMessage: emailRequiredErrorMessage,
        },
        company: {
            id: field3Name,
            label: field3Label,
            required: !field3Optional,
            invalidErrorMessage: field3InvalidErrorMessage,
            requiredErrorMessage: field3RequiredErrorMessage,
        },
        field4: {
            id: field4Name,
            label: field4Label,
            required: !field4Optional,
            requiredErrorMessage: field4RequiredErrorMessage,
        },
        dropdown1: {
            id: dropdown1Name,
            label: dropdown1Label,
            placeholder: DROPDOWN_PLACEHOLDER_TEXT,
            options: dropdown1Options,
            required: dropdownRequired(dropdown1Options, dropdown1Optional),
            requiredErrorMessage: dropdown1RequiredErrorMessage,
        },
        dropdown2: {
            id: dropdown2Name,
            label: dropdown2Label,
            placeholder: DROPDOWN_PLACEHOLDER_TEXT,
            options: dropdown2Options,
            required: dropdownRequired(dropdown2Options, dropdown2Optional),
            requiredErrorMessage: dropdown2RequiredErrorMessage,
        },
        dropdown3: {
            id: dropdown3Name,
            label: dropdown3Label,
            placeholder: DROPDOWN_PLACEHOLDER_TEXT,
            options: dropdown3Options,
            otherOption: dropdown3OtherOption,
            required: dropdownRequired(dropdown3Options, dropdown3Optional),
            requiredErrorMessage: dropdown3RequiredErrorMessage,
        },
        otherDetails: {
            id: dropdown3OtherDetailsFieldName,
            label: dropdown3OtherDetailsFieldLabel,
            placeholder: dropdown3OtherDetailsFieldPlaceholder,
            requiredErrorMessage: dropdown3OtherDetailsFieldRequiredErrorMessage,
        },
        multiSelectionCheckbox: {
            id: multiSelectionCheckboxName,
            label: multiSelectionCheckboxLabel,
            options: multiSelectionCheckboxOptions,
            required: !multiSelectionCheckboxOptional,
            requiredErrorMessage: multiSelectionCheckboxRequiredErrorMessage,
            type: 'multiSelectionCheckbox',
        },
        newsletterPrompt: {
            type: 'checkbox',
            id: 'consent',
            label: newsletterPromptLabel,
            required: false,
        },
    };

    const fieldsKeysArray = Object.keys(fields);

    const [input, setInput] = useState(getInitialInputState(fields, fieldsKeysArray));
    const [submitting, setSubmitting] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitSuccessful, setSubmitSuccessful] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [nonFieldErrors, setNonFieldErrors] = useState([]);

    const formRef = useRef();

    const handleInputChange = (event) => {
        const target = event.target;
        const name = target.name;
        let value = target.value;
        if (target.type === 'checkbox' && target.hasAttribute('data-ismultiselect')) {
            if (target.checked) {
                value = [...input[fields.multiSelectionCheckbox.id].value, target.value];
            } else {
                value = input[fields.multiSelectionCheckbox.id].value.filter(
                    (value) => value !== target.value
                );
            }
        } else if (target.type === 'checkbox') {
            value = target.checked;
        }

        const key = fieldsKeysArray.find((key) => fields[key].id === name);
        const fieldError = fieldErrors[name];
        const label = fields[key].label;

        const validation = validateInput(fields, key, target, value, fieldError, label, input);

        setInput({
            ...input,
            ...(fields.otherDetails.id && {
                [fields.otherDetails.id]: {
                    ...input[fields.otherDetails.id],
                    ...setOtherDetails({
                        currentFieldName: name,
                        parentField: fields.dropdown3,
                        parentValue: value,
                    }),
                },
            }),
            [name]: {
                ...input[name],
                ...validation,
                value: value,
                serverErrorMessage: '', // Clear the field's server error message
            },
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        // Prevent the user from firing multiple requests when clicking multiple times:
        if (submitting) {
            return;
        }

        setSubmitting(true);

        const target = event.target;
        const validatedForm = validateForm(
            fields,
            fieldsKeysArray,
            target,
            input,
            submitAttempted,
            fieldErrors
        );
        const submittedFields = validatedForm.submittedFields;
        const isFormValid = validatedForm.isFormValid;

        // Clear all fields' server error messages:
        fieldsKeysArray.forEach((key) => {
            const name = fields[key].id;
            const label = fields[key].label;

            if (!!name && !!label) {
                submittedFields[name].serverErrorMessage = '';
            }
        });

        if (!submitAttempted) {
            setSubmitAttempted(true);
        }

        if (isFormValid) {
            const leadId = uuidv4();
            (async function () {
                let data = {};
                const submittedFieldsKeys = Object.keys(submittedFields);

                submittedFieldsKeys.forEach((key) => {
                    data[key] = submittedFields[key].value;
                });
                data[LEAD_ID_KEY] = leadId;

                try {
                    // Form data post request:
                    const contactRecaptchaToken = await getRecaptchaToken();
                    const postData = getPostData(
                        path,
                        contactRecaptchaToken,
                        submittedFields,
                        leadId
                    );
                    await axios.post(path + '.contact.json', postData);

                    const consentProvided = submittedFields.consent.value;

                    try {
                        const subscribeRecaptchaToken = await getRecaptchaToken();

                        // Email signup:
                        await axios
                            .post(path + '.subscribe.json', {
                                path: path,
                                recaptchaToken: subscribeRecaptchaToken,
                                data: {
                                    email: submittedFields.email.value,
                                    consentProvided: consentProvided,
                                    status: consentProvided
                                        ? MAILCHIMP_SUBSCRIPTION_STATUS.SUBSCRIBED
                                        : MAILCHIMP_SUBSCRIPTION_STATUS.UNSUBSCRIBED,
                                    locale: document.documentElement.lang,
                                    merge_fields: { ...getMergeFieldsData(data) },
                                },
                            })
                            .catch((error) => {
                                const { response } = error;
                                if (
                                    response &&
                                    response.data &&
                                    response.data.nonFieldErrors &&
                                    Array.isArray(response.data.nonFieldErrors) &&
                                    response.data.nonFieldErrors.some(
                                        (error) => error === ERROR_CODES.MEMBER_EXISTS
                                    )
                                )
                                    return response.data;
                                throw error;
                            });

                        // If both form data post request and email signup did not throw an error, then:
                        dataLayerService.submitForm(formName, true, data);

                        setInput(submittedFields);
                        setSubmitting(false);
                        setSubmitted(true);
                        setSubmitSuccessful(true);
                        setFieldErrors({});
                        setNonFieldErrors([]);

                        // ...and download the PDF:
                        const link = document.createElement('a');
                        link.setAttribute('href', pdfPath);
                        link.setAttribute('download', '');
                        link.setAttribute('target', '_blank');
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    } catch (error) {
                        // If email signup threw an error:
                        dataLayerService.submitForm(formName, false, data);

                        handleErrors(
                            error,
                            setFieldErrors,
                            setNonFieldErrors,
                            setInput,
                            submittedFields
                        );

                        setSubmitting(false);
                        setSubmitted(true);
                        setSubmitSuccessful(false);
                    }
                } catch (error) {
                    // If form data post request threw an error:
                    dataLayerService.submitForm(formName, false, data);

                    handleErrors(
                        error,
                        setFieldErrors,
                        setNonFieldErrors,
                        setInput,
                        submittedFields
                    );

                    setSubmitting(false);
                    setSubmitted(true);
                    setSubmitSuccessful(false);
                }
            })();
        } else {
            setFieldErrors({});
            setInput(submittedFields);
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (fieldErrors) {
            const validatedForm = validateForm(
                fields,
                fieldsKeysArray,
                formRef.current,
                input,
                submitAttempted,
                fieldErrors
            );
            const submittedFields = validatedForm.submittedFields;

            setInput(submittedFields);
        }
    }, [fieldErrors, fields, fieldsKeysArray, input, submitAttempted]);

    const returnToForm = () => {
        if (submitSuccessful) {
            setInput(getInitialInputState(fields, fieldsKeysArray));

            setSubmitAttempted(false);
        }

        setSubmitted(false);
        setSubmitSuccessful(false);
    };

    useEffect(() => {
        loadRecaptcha();
    }, []);

    return (
        <section className={cx('hero', styles.root)} id="hero">
            <div className={styles.container}>
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.subcontainer}>
                    <div className={styles.content}>
                        <h2 className={styles.subtitle}>{subtitle}</h2>
                        <div className={cx('light-theme', styles['content-container'])}>
                            {children}
                        </div>
                    </div>
                    <div className={cx('light-theme', styles['form-container'])}>
                        <ContactUsFormSubmissionResult
                            className={submitted ? undefined : styles.hide}
                            submitted={submitted}
                            returnToForm={returnToForm}
                            submitErrorTitle={submitErrorTitle}
                            submitErrorText={nonFieldErrors
                                .map((error) => FORM_ERROR_CODES[error])
                                .join('<br />')}
                            submitErrorCtaText={submitErrorCtaText}
                            submitErrorCtaAltText={submitErrorCtaAltText}
                            submitSuccessful={submitSuccessful}
                            submitSuccessTitle={submitSuccessTitle}
                            submitSuccessText={submitSuccessText}
                            submitSuccessCtaText={submitSuccessCtaText}
                            submitSuccessCtaAltText={submitSuccessCtaAltText}
                        />
                        <form
                            className={cx(styles.form, { [styles.hide]: submitted })}
                            onSubmit={handleSubmit}
                            ref={formRef}
                            noValidate
                        >
                            <h3 className={styles['form-heading']}>{formHeading}</h3>
                            {formSubheading && (
                                <p className={styles['form-subheading']}>{formSubheading}</p>
                            )}
                            <div className={styles['form-fields']}>
                                <div
                                    className={cx(styles['form-fields-container'], {
                                        [styles.hidden]: submitted,
                                    })}
                                >
                                    {((field1Label && field1RequiredErrorMessage) ||
                                        (field2Label && field2RequiredErrorMessage)) && (
                                        <div className={styles.row}>
                                            {field1Label && field1RequiredErrorMessage && (
                                                <TextInput
                                                    containerClassName={cx(
                                                        styles['input-container'],
                                                        {
                                                            [styles['input-container--full-width']]:
                                                                !field2Label,
                                                        }
                                                    )}
                                                    errorMessage={
                                                        input[fields.field1.id].errorMessage
                                                    }
                                                    serverErrorMessage={
                                                        input[fields.field1.id].serverErrorMessage
                                                    }
                                                    fieldClassName={styles['text-field']}
                                                    handleInputChange={handleInputChange}
                                                    id={fields.field1.id}
                                                    isInvalid={
                                                        submitAttempted
                                                            ? input[fields.field1.id].isInvalid
                                                            : false
                                                    }
                                                    required={fields.field1.required}
                                                    title={fields.field1.label}
                                                    value={input[fields.field1.id].value}
                                                />
                                            )}
                                            {field2Label && field2RequiredErrorMessage && (
                                                <TextInput
                                                    containerClassName={cx(
                                                        styles['input-container'],
                                                        {
                                                            [styles['input-container--full-width']]:
                                                                !field1Label,
                                                        }
                                                    )}
                                                    errorMessage={
                                                        input[fields.field2.id].errorMessage
                                                    }
                                                    serverErrorMessage={
                                                        input[fields.field2.id].serverErrorMessage
                                                    }
                                                    fieldClassName={styles['text-field']}
                                                    handleInputChange={handleInputChange}
                                                    id={fields.field2.id}
                                                    isInvalid={
                                                        submitAttempted
                                                            ? input[fields.field2.id].isInvalid
                                                            : false
                                                    }
                                                    required={fields.field2.required}
                                                    title={fields.field2.label}
                                                    value={input[fields.field2.id].value}
                                                />
                                            )}
                                        </div>
                                    )}
                                    {((phoneLabel &&
                                        phoneRequiredErrorMessage &&
                                        phoneInvalidErrorMessage) ||
                                        (emailLabel &&
                                            emailRequiredErrorMessage &&
                                            emailInvalidErrorMessage)) && (
                                        <div className={styles.row}>
                                            {phoneLabel &&
                                                phoneRequiredErrorMessage &&
                                                phoneInvalidErrorMessage && (
                                                    <TextInput
                                                        containerClassName={cx(
                                                            styles['input-container'],
                                                            {
                                                                [styles[
                                                                    'input-container--full-width'
                                                                ]]: !emailLabel,
                                                            }
                                                        )}
                                                        errorMessage={
                                                            input[fields.phone.id].errorMessage
                                                        }
                                                        serverErrorMessage={
                                                            input[fields.phone.id]
                                                                .serverErrorMessage
                                                        }
                                                        fieldClassName={styles['text-field']}
                                                        handleInputChange={handleInputChange}
                                                        id={fields.phone.id}
                                                        isInvalid={
                                                            submitAttempted
                                                                ? input[fields.phone.id].isInvalid
                                                                : false
                                                        }
                                                        required={fields.phone.required}
                                                        title={fields.phone.label}
                                                        type="tel"
                                                        placeholder={fields.phone.placeholder}
                                                        mask={PHONE_MASK}
                                                        value={input[fields.phone.id].value}
                                                    />
                                                )}
                                            {emailLabel &&
                                                emailRequiredErrorMessage &&
                                                emailInvalidErrorMessage && (
                                                    <TextInput
                                                        containerClassName={cx(
                                                            styles['input-container'],
                                                            {
                                                                [styles[
                                                                    'input-container--full-width'
                                                                ]]: !phoneLabel,
                                                            }
                                                        )}
                                                        errorMessage={
                                                            input[fields.email.id].errorMessage
                                                        }
                                                        serverErrorMessage={
                                                            input[fields.email.id]
                                                                .serverErrorMessage
                                                        }
                                                        fieldClassName={styles['text-field']}
                                                        handleInputChange={handleInputChange}
                                                        id={fields.email.id}
                                                        isInvalid={
                                                            submitAttempted
                                                                ? input[fields.email.id].isInvalid
                                                                : false
                                                        }
                                                        required={fields.email.required}
                                                        title={fields.email.label}
                                                        type="email"
                                                        value={input[fields.email.id].value}
                                                    />
                                                )}
                                        </div>
                                    )}
                                    {((field3Label && field3RequiredErrorMessage) ||
                                        (field4Label && field4RequiredErrorMessage)) && (
                                        <div className={styles.row}>
                                            {field3Label && field3RequiredErrorMessage && (
                                                <TextInput
                                                    containerClassName={cx(
                                                        styles['input-container'],
                                                        {
                                                            [styles['input-container--full-width']]:
                                                                !field4Label,
                                                        }
                                                    )}
                                                    errorMessage={
                                                        input[fields.company.id].errorMessage
                                                    }
                                                    serverErrorMessage={
                                                        input[fields.company.id].serverErrorMessage
                                                    }
                                                    fieldClassName={styles['text-field']}
                                                    handleInputChange={handleInputChange}
                                                    id={fields.company.id}
                                                    isInvalid={
                                                        submitAttempted
                                                            ? input[fields.company.id].isInvalid
                                                            : false
                                                    }
                                                    required={fields.company.required}
                                                    title={fields.company.label}
                                                    value={input[fields.company.id].value}
                                                />
                                            )}
                                            {field4Label && field4RequiredErrorMessage && (
                                                <TextInput
                                                    containerClassName={cx(
                                                        styles['input-container'],
                                                        {
                                                            [styles['input-container--full-width']]:
                                                                !field3Label,
                                                        }
                                                    )}
                                                    errorMessage={
                                                        input[fields.field4.id].errorMessage
                                                    }
                                                    serverErrorMessage={
                                                        input[fields.field4.id].serverErrorMessage
                                                    }
                                                    fieldClassName={styles['text-field']}
                                                    handleInputChange={handleInputChange}
                                                    id={fields.field4.id}
                                                    isInvalid={
                                                        submitAttempted
                                                            ? input[fields.field4.id].isInvalid
                                                            : false
                                                    }
                                                    required={fields.field4.required}
                                                    title={fields.field4.label}
                                                    value={input[fields.field4.id].value}
                                                />
                                            )}
                                        </div>
                                    )}
                                    {multiSelectionCheckboxLabel &&
                                        multiSelectionCheckboxOptions &&
                                        multiSelectionCheckboxOptions.length > 0 && (
                                            <div className={cx(styles['row'])}>
                                                <div
                                                    className={cx(
                                                        styles['input-container'],
                                                        styles['input-container--full-width']
                                                    )}
                                                >
                                                    <MultiSelectionCheckbox
                                                        handleInputChange={handleInputChange}
                                                        id={fields.multiSelectionCheckbox.id}
                                                        isInvalid={
                                                            submitAttempted
                                                                ? input[
                                                                      fields.multiSelectionCheckbox
                                                                          .id
                                                                  ].isInvalid
                                                                : false
                                                        }
                                                        errorMessage={
                                                            input[fields.multiSelectionCheckbox.id]
                                                                .errorMessage
                                                        }
                                                        serverErrorMessage={
                                                            input[fields.multiSelectionCheckbox.id]
                                                                .serverErrorMessage
                                                        }
                                                        required={
                                                            fields.multiSelectionCheckbox.required
                                                        }
                                                        title={fields.multiSelectionCheckbox.label}
                                                        value={
                                                            input[fields.multiSelectionCheckbox.id]
                                                                .value
                                                        }
                                                        checkboxSelectionOptions={
                                                            fields.multiSelectionCheckbox.options
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    {dropdown1Label &&
                                        dropdown1Options &&
                                        dropdown1RequiredErrorMessage && (
                                            <div className={styles.row}>
                                                {dropdown1Label &&
                                                    dropdown1Options &&
                                                    dropdown1RequiredErrorMessage && (
                                                        <SelectInput
                                                            containerClassName={cx(
                                                                styles['input-container'],
                                                                styles[
                                                                    'input-container--full-width'
                                                                ]
                                                            )}
                                                            errorMessage={
                                                                input[fields.dropdown1.id]
                                                                    .errorMessage
                                                            }
                                                            serverErrorMessage={
                                                                input[fields.dropdown1.id]
                                                                    .serverErrorMessage
                                                            }
                                                            fieldClassName={styles['select-field']}
                                                            handleInputChange={handleInputChange}
                                                            id={fields.dropdown1.id}
                                                            isInvalid={
                                                                submitAttempted
                                                                    ? input[fields.dropdown1.id]
                                                                          .isInvalid
                                                                    : false
                                                            }
                                                            options={fields.dropdown1.options}
                                                            placeholder={
                                                                fields.dropdown1.placeholder
                                                            }
                                                            required={fields.dropdown1.required}
                                                            title={fields.dropdown1.label}
                                                            value={input[fields.dropdown1.id].value}
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    {dropdown2Label &&
                                        dropdown2Options &&
                                        dropdown2RequiredErrorMessage && (
                                            <div className={styles.row}>
                                                {dropdown2Label &&
                                                    dropdown2Options &&
                                                    dropdown2RequiredErrorMessage && (
                                                        <SelectInput
                                                            containerClassName={cx(
                                                                styles['input-container'],
                                                                styles[
                                                                    'input-container--full-width'
                                                                ]
                                                            )}
                                                            errorMessage={
                                                                input[fields.dropdown2.id]
                                                                    .errorMessage
                                                            }
                                                            serverErrorMessage={
                                                                input[fields.dropdown2.id]
                                                                    .serverErrorMessage
                                                            }
                                                            fieldClassName={styles['select-field']}
                                                            handleInputChange={handleInputChange}
                                                            id={fields.dropdown2.id}
                                                            isInvalid={
                                                                submitAttempted
                                                                    ? input[fields.dropdown2.id]
                                                                          .isInvalid
                                                                    : false
                                                            }
                                                            options={fields.dropdown2.options}
                                                            placeholder={
                                                                fields.dropdown2.placeholder
                                                            }
                                                            required={fields.dropdown2.required}
                                                            title={fields.dropdown2.label}
                                                            value={input[fields.dropdown2.id].value}
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    {dropdown3Label &&
                                        dropdown3Options &&
                                        dropdown3RequiredErrorMessage && (
                                            <div className={styles.row}>
                                                {dropdown3Label &&
                                                    dropdown3Options &&
                                                    dropdown3RequiredErrorMessage && (
                                                        <SelectInput
                                                            containerClassName={cx(
                                                                styles['input-container'],
                                                                styles[
                                                                    'input-container--full-width'
                                                                ]
                                                            )}
                                                            errorMessage={
                                                                input[fields.dropdown3.id]
                                                                    .errorMessage
                                                            }
                                                            serverErrorMessage={
                                                                input[fields.dropdown3.id]
                                                                    .serverErrorMessage
                                                            }
                                                            fieldClassName={styles['select-field']}
                                                            handleInputChange={handleInputChange}
                                                            id={fields.dropdown3.id}
                                                            isInvalid={
                                                                submitAttempted
                                                                    ? input[fields.dropdown3.id]
                                                                          .isInvalid
                                                                    : false
                                                            }
                                                            options={fields.dropdown3.options}
                                                            otherOption={
                                                                fields.dropdown3.otherOption
                                                            }
                                                            placeholder={
                                                                fields.dropdown3.placeholder
                                                            }
                                                            required={fields.dropdown3.required}
                                                            title={fields.dropdown3.label}
                                                            value={input[fields.dropdown3.id].value}
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    {input[fields.otherDetails.id] &&
                                        input[fields.otherDetails.id].isVisible && (
                                            <div className={cx(styles['row'])}>
                                                <TextInput
                                                    containerClassName={cx(
                                                        styles['input-container'],
                                                        styles['input-container--full-width'],
                                                        styles['row--other-checkbox']
                                                    )}
                                                    errorMessage={
                                                        input[fields.otherDetails.id].errorMessage
                                                    }
                                                    serverErrorMessage={
                                                        input[fields.otherDetails.id]
                                                            .serverErrorMessage
                                                    }
                                                    fieldClassName={styles['text-field']}
                                                    handleInputChange={handleInputChange}
                                                    id={fields.otherDetails.id}
                                                    isInvalid={
                                                        input[fields.otherDetails.id].isInvalid
                                                    }
                                                    required={
                                                        input[fields.otherDetails.id].isRequired
                                                    }
                                                    title={fields.otherDetails.label}
                                                    value={input[fields.otherDetails.id].value}
                                                    placeholder={fields.otherDetails.placeholder}
                                                />
                                            </div>
                                        )}

                                    {newsletterPromptLabel && (
                                        <div className={styles['row--checkbox']}>
                                            <div
                                                className={cx(
                                                    styles['input-container'],
                                                    styles['input-container--full-width'],
                                                    styles['input-container--checkbox']
                                                )}
                                            >
                                                <input
                                                    className={styles['checkbox-input']}
                                                    id={fields.newsletterPrompt.id}
                                                    name={fields.newsletterPrompt.id}
                                                    onChange={handleInputChange}
                                                    type="checkbox"
                                                    checked={
                                                        input[fields.newsletterPrompt.id].value
                                                    }
                                                />
                                                <label
                                                    className={styles['checkbox-label']}
                                                    htmlFor={fields.newsletterPrompt.id}
                                                >
                                                    {newsletterPromptLabel}
                                                    {fields.newsletterPrompt.required && ' *'}
                                                </label>
                                            </div>
                                            {input[fields.newsletterPrompt.id]
                                                .serverErrorMessage && (
                                                <div
                                                    className={cx(
                                                        styles['error-message-container']
                                                    )}
                                                >
                                                    <Icon
                                                        className={cx(styles['error-message-icon'])}
                                                        embedSVG={true}
                                                        size="x-small"
                                                        url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/attention.svg"
                                                    />
                                                    <div
                                                        className={styles['error-message-text']}
                                                        dangerouslySetInnerHTML={{
                                                            __html: input[
                                                                fields.newsletterPrompt.id
                                                            ].serverErrorMessage,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <Button
                                        altText={
                                            ctaAltText ||
                                            t('fs.pdf-download-hero.submit-cta-alt-text-default')
                                        }
                                        className={styles.cta}
                                        linkText={ctaText}
                                        type="form"
                                    />
                                    <Text
                                        className={styles['recaptcha-text']}
                                        content={RECAPTCHA_BRANDING_TEXT}
                                        size="small"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

PDFDownloadHero.displayName = 'PdfDownloadHero';

PDFDownloadHero.defaultProps = {
    formName: 'pdf-download-hero',
};

PDFDownloadHero.propTypes = {
    ctaAltText: PropTypes.string,
    ctaText: PropTypes.string.isRequired,
    dropdown1Label: PropTypes.string,
    dropdown1Name: PropTypes.string,
    dropdown1Options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ),
    dropdown1Optional: PropTypes.bool,
    dropdown1RequiredErrorMessage: PropTypes.string,
    dropdown2Label: PropTypes.string,
    dropdown2Name: PropTypes.string,
    dropdown2Options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ),
    dropdown2Optional: PropTypes.bool,
    dropdown2RequiredErrorMessage: PropTypes.string,
    dropdown3Label: PropTypes.string,
    dropdown3Name: PropTypes.string,
    dropdown3Options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ),
    dropdown3OtherOption: PropTypes.bool,
    dropdown3Optional: PropTypes.bool,
    dropdown3RequiredErrorMessage: PropTypes.string,
    dropdown3OtherDetailsFieldLabel: PropTypes.string,
    dropdown3OtherDetailsFieldName: PropTypes.string,
    dropdown3OtherDetailsFieldPlaceholder: PropTypes.string,
    dropdown3OtherDetailsFieldRequiredErrorMessage: PropTypes.string,
    emailLabel: PropTypes.string.isRequired,
    emailInvalidErrorMessage: PropTypes.string.isRequired,
    emailRequiredErrorMessage: PropTypes.string.isRequired,
    field1Label: PropTypes.string,
    field1Name: PropTypes.string,
    field1Optional: PropTypes.bool,
    field1RequiredErrorMessage: PropTypes.string,
    field2Label: PropTypes.string,
    field2Name: PropTypes.string,
    field2Optional: PropTypes.bool,
    field2RequiredErrorMessage: PropTypes.string,
    field3Label: PropTypes.string,
    field3Name: PropTypes.string,
    field3Optional: PropTypes.bool,
    field3RequiredErrorMessage: PropTypes.string,
    field3InvalidErrorMessage: PropTypes.string,
    field4Label: PropTypes.string,
    field4Name: PropTypes.string,
    field4Optional: PropTypes.bool,
    field4RequiredErrorMessage: PropTypes.string,
    formHeading: PropTypes.string.isRequired,
    formName: PropTypes.string.isRequired,
    formSubheading: PropTypes.string,
    newsletterPromptLabel: PropTypes.string,
    path: PropTypes.string.isRequired,
    pdfPath: PropTypes.string.isRequired,
    phoneLabel: PropTypes.string,
    phoneName: PropTypes.string,
    phoneOptional: PropTypes.bool,
    phonePlaceholder: PropTypes.string,
    phoneInvalidErrorMessage: PropTypes.string,
    phoneRequiredErrorMessage: PropTypes.string,
    submitErrorTitle: PropTypes.string.isRequired,
    submitErrorCtaText: PropTypes.string.isRequired,
    submitErrorCtaAltText: PropTypes.string,
    submitSuccessTitle: PropTypes.string.isRequired,
    submitSuccessText: PropTypes.string.isRequired,
    submitSuccessCtaText: PropTypes.string.isRequired,
    submitSuccessCtaAltText: PropTypes.string,
    subtitle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    checkboxSelectionLabel: PropTypes.string,
    checkboxSelectionName: PropTypes.string,
    checkboxSelectionOptions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ),
    checkboxSelectionOptional: PropTypes.bool,
    checkboxSelectionRequiredErrorMessage: PropTypes.string,
};

export default PDFDownloadHero;
